import { Link } from "react-router-dom";
import { useSession } from "@/lib/auth-client";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import { styled } from "@mui/material/styles";
import { Users, Building2, Briefcase } from "lucide-react";
import { useContacts } from "@/hooks/use-contacts";
import { useAuthContext } from "@/components/auth";
import { HealthStatusCard } from "@/components/admin/HealthStatusCard";
import { HealthHistoryCard } from "@/components/admin/HealthHistoryCard";
import { JobStatsCard } from "@/components/admin/JobStatsCard";
import { PauseResumeButton } from "@/components/admin/PauseResumeButton";
import { useSystemHealth, useHealthHistory } from "@/hooks/admin/use-system-health";
import { useAdminJobs } from "@/hooks/admin/use-admin-jobs";
import { useTenants } from "@/hooks/admin/use-tenants";
import { useAdminUsers } from "@/hooks/admin/use-admin-users";

// Map tenant slugs to logo paths
const TENANT_LOGOS: Record<string, string> = {
  wallace: "/logos/wallace.png",
  sothebys: "/logos/sothebys.png",
};

const TenantLogo = styled("img")({
  height: 80,
  width: "auto",
  objectFit: "contain",
  marginBottom: 24,
});

function formatUptime(seconds: number): string {
  const secs = Math.floor(seconds);
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  const hours = Math.floor(secs / 3600);
  const minutes = Math.floor((secs % 3600) / 60);
  if (hours < 24) return `${hours}h ${minutes}m`;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return `${days}d ${remainingHours}h`;
}

// Helper to map component health status to card status
function mapHealthStatus(status?: 'pass' | 'warn' | 'fail'): 'healthy' | 'warning' | 'error' {
  if (status === 'pass') return 'healthy';
  if (status === 'warn') return 'warning';
  return 'error';
}

function AdminSection() {
  const { data: health, isLoading: healthLoading } = useSystemHealth();
  const { data: history, isLoading: historyLoading } = useHealthHistory(20);
  const { data: jobs, isLoading: jobsLoading } = useAdminJobs();
  const { data: tenants, isLoading: tenantsLoading } = useTenants();
  const { data: users, isLoading: usersLoading } = useAdminUsers();

  return (
    <Box sx={{ mt: 4, pt: 4, borderTop: 1, borderColor: "divider" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Admin Overview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            System management {health?.version && `(v${health.version})`}
          </Typography>
        </Box>
        <PauseResumeButton />
      </Box>

      {/* System Health */}
      <Box component="section" sx={{ mb: 4 }}>
        <Typography
          variant="body2"
          fontWeight={500}
          color="text.secondary"
          sx={{ mb: 1.5 }}
        >
          System Health
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 2 }}>
            <HealthStatusCard
              title="Database"
              status={mapHealthStatus(health?.checks?.database?.status)}
              latency={health?.checks?.database?.latency}
              message={health?.checks?.database?.message}
              loading={healthLoading}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <HealthStatusCard
              title="Redis"
              status={mapHealthStatus(health?.checks?.redis?.status)}
              latency={health?.checks?.redis?.latency}
              message={health?.checks?.redis?.message}
              loading={healthLoading}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <HealthStatusCard
              title="REINZ Token"
              status={mapHealthStatus(health?.checks?.reinzToken?.status)}
              latency={health?.checks?.reinzToken?.latency}
              message={health?.checks?.reinzToken?.message}
              loading={healthLoading}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <HealthStatusCard
              title="Mailgun"
              status={mapHealthStatus(health?.checks?.mailgun?.status)}
              message={health?.checks?.mailgun?.message}
              loading={healthLoading}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <HealthStatusCard
              title="Report Queue"
              status={mapHealthStatus(health?.checks?.queueDepth?.status)}
              latency={health?.checks?.queueDepth?.latency}
              message={health?.checks?.queueDepth?.message}
              loading={healthLoading}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <HealthStatusCard
              title="Email Queue"
              status={mapHealthStatus(health?.checks?.emailQueueDepth?.status)}
              latency={health?.checks?.emailQueueDepth?.latency}
              message={health?.checks?.emailQueueDepth?.message}
              loading={healthLoading}
            />
          </Grid>
        </Grid>
      </Box>

      {/* System Metrics */}
      <Box component="section" sx={{ mb: 4 }}>
        <Typography
          variant="body2"
          fontWeight={500}
          color="text.secondary"
          sx={{ mb: 1.5 }}
        >
          System Metrics
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card variant="outlined" sx={{ p: 2 }}>
              {healthLoading ? (
                <>
                  <Skeleton variant="text" width={80} />
                  <Skeleton
                    variant="rectangular"
                    height={8}
                    sx={{ mt: 1, borderRadius: 1 }}
                  />
                  <Skeleton variant="text" width={60} sx={{ mt: 0.5 }} />
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Memory (RSS)
                  </Typography>
                  <Box
                    sx={{
                      mt: 1,
                      bgcolor: "grey.200",
                      borderRadius: 1,
                      height: 8,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${Math.min(health?.memory?.percentage ?? 0, 100)}%`,
                        bgcolor:
                          (health?.memory?.percentage ?? 0) > 90
                            ? "error.main"
                            : (health?.memory?.percentage ?? 0) > 75
                              ? "warning.main"
                              : "success.main",
                        transition: "width 0.3s ease",
                      }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {health?.memory?.usedMB ?? 0} MB / {health?.memory?.limitMB ?? 512} MB
                  </Typography>
                </>
              )}
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card variant="outlined" sx={{ p: 2 }}>
              {healthLoading ? (
                <>
                  <Skeleton variant="text" width={80} />
                  <Skeleton variant="text" width={100} height={32} />
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Uptime
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color="text.primary"
                  >
                    {formatUptime(health?.uptime ?? 0)}
                  </Typography>
                </>
              )}
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              variant="outlined"
              sx={{
                p: 2,
                bgcolor:
                  health?.status === "healthy"
                    ? "success.lighter"
                    : health?.status === "degraded"
                      ? "warning.lighter"
                      : "error.lighter",
                borderColor:
                  health?.status === "healthy"
                    ? "success.light"
                    : health?.status === "degraded"
                      ? "warning.light"
                      : "error.light",
              }}
            >
              {healthLoading ? (
                <>
                  <Skeleton variant="text" width={80} />
                  <Skeleton variant="text" width={100} height={32} />
                </>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        health?.status === "healthy"
                          ? "success.dark"
                          : health?.status === "degraded"
                            ? "warning.dark"
                            : "error.dark",
                    }}
                  >
                    Overall Status
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{
                      color:
                        health?.status === "healthy"
                          ? "success.dark"
                          : health?.status === "degraded"
                            ? "warning.dark"
                            : "error.dark",
                      textTransform: "capitalize",
                    }}
                  >
                    {health?.status === "healthy"
                      ? "All Systems Operational"
                      : health?.status === "degraded"
                        ? "Degraded Performance"
                        : "Issues Detected"}
                  </Typography>
                </>
              )}
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Health History */}
      <Box component="section" sx={{ mb: 4 }}>
        <HealthHistoryCard
          events={history?.events ?? []}
          loading={historyLoading}
        />
      </Box>

      {/* Job Queue */}
      <Box component="section" sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.5,
          }}
        >
          <Typography variant="body2" fontWeight={500} color="text.secondary">
            Job Queue
          </Typography>
          <Typography
            component={Link}
            to="/jobs"
            variant="body2"
            color="primary"
            sx={{
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            View all jobs →
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 3 }}>
            <JobStatsCard
              title="Pending"
              value={jobs?.pending ?? 0}
              color="info"
              loading={jobsLoading}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <JobStatsCard
              title="Active"
              value={jobs?.active ?? 0}
              color="warning"
              loading={jobsLoading}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <JobStatsCard
              title="Completed"
              value={jobs?.completed ?? 0}
              color="success"
              loading={jobsLoading}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <JobStatsCard
              title="Failed"
              value={jobs?.failed ?? 0}
              color="error"
              loading={jobsLoading}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Admin Quick Links */}
      <Box component="section">
        <Typography
          variant="body2"
          fontWeight={500}
          color="text.secondary"
          sx={{ mb: 1.5 }}
        >
          Management
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              component={Link}
              to="/tenants"
              variant="outlined"
              sx={{
                p: 3,
                display: "flex",
                alignItems: "center",
                gap: 2,
                textDecoration: "none",
                transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: 2 },
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "secondary.lighter",
                  borderRadius: 2,
                  display: "flex",
                  color: "secondary.main",
                }}
              >
                <Building2 size={24} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} color="text.primary">
                  {tenantsLoading ? "..." : (tenants?.length ?? 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tenants
                </Typography>
              </Box>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              component={Link}
              to="/users"
              variant="outlined"
              sx={{
                p: 3,
                display: "flex",
                alignItems: "center",
                gap: 2,
                textDecoration: "none",
                transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: 2 },
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "primary.lighter",
                  borderRadius: 2,
                  display: "flex",
                  color: "primary.main",
                }}
              >
                <Users size={24} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} color="text.primary">
                  {usersLoading ? "..." : (users?.length ?? 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Users
                </Typography>
              </Box>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              component={Link}
              to="/jobs"
              variant="outlined"
              sx={{
                p: 3,
                display: "flex",
                alignItems: "center",
                gap: 2,
                textDecoration: "none",
                transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: 2 },
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "success.lighter",
                  borderRadius: 2,
                  display: "flex",
                  color: "success.main",
                }}
              >
                <Briefcase size={24} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} color="text.primary">
                  {jobsLoading
                    ? "..."
                    : (jobs?.active ?? 0) + (jobs?.pending ?? 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Jobs
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export function Dashboard() {
  const { data: session } = useSession();
  const { isSuperAdmin, tenant } = useAuthContext();
  const { data: contacts, isLoading } = useContacts();

  // Get first name from session user name
  const firstName = session?.user?.name?.split(" ")[0] || "User";

  // Get tenant logo based on slug
  const tenantLogo = tenant?.slug ? TENANT_LOGOS[tenant.slug] : null;

  return (
    <Box>
      {tenantLogo && (
        <TenantLogo src={tenantLogo} alt={tenant?.name || "Partner"} />
      )}
      <Typography
        variant="h5"
        fontWeight={700}
        color="text.primary"
        sx={{ mb: 3 }}
      >
        Welcome, {firstName}
      </Typography>

      {/* User Stats - visible to all */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined" sx={{ p: 3 }}>
            <Typography variant="body2" fontWeight={500} color="text.secondary">
              Total Contacts
            </Typography>
            <Typography
              variant="h4"
              fontWeight={700}
              color="text.primary"
              sx={{ mt: 1 }}
            >
              {isLoading ? <Skeleton width={48} /> : (contacts?.length ?? 0)}
            </Typography>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined" sx={{ p: 3 }}>
            <Typography variant="body2" fontWeight={500} color="text.secondary">
              Active Contacts
            </Typography>
            <Typography
              variant="h4"
              fontWeight={700}
              color="text.primary"
              sx={{ mt: 1 }}
            >
              {isLoading ? (
                <Skeleton width={48} />
              ) : (
                (contacts?.filter((v) => v.isActive).length ?? 0)
              )}
            </Typography>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined" sx={{ p: 3 }}>
            <Typography variant="body2" fontWeight={500} color="text.secondary">
              Reports Sent
            </Typography>
            <Typography
              variant="h4"
              fontWeight={700}
              color="text.primary"
              sx={{ mt: 1 }}
            >
              -
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Admin Section - only visible to super admins */}
      {isSuperAdmin && <AdminSection />}
    </Box>
  );
}
